import WallpaperRoundedIcon from '@mui/icons-material/WallpaperRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, IconButton, Stack, styled, Tooltip, Typography, useMediaQuery, useTheme, Zoom } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LabVariantData } from '../../AppData/LabData';
import SizeDisplay from '../../Common/Measurements/SizeDisplay';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import useLabData from '../_hooks/useLabData';

const GuidelinesOverlay = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(238, 238, 238, 0.86)',
  borderRadius: 18,
  color: theme.palette.text.primary,
  left: 16,
  maxWidth: 320,
  padding: theme.spacing(1.25, 1.5),
  pointerEvents: 'auto',
  position: 'absolute',
  top: 16,
  width: 'min(320px, calc(100% - 32px))',
  zIndex: 22,

  [theme.breakpoints.down('sm')]: {
    left: 12,
    padding: theme.spacing(1, 1.25),
    top: 12,
    width: 'min(300px, calc(100% - 24px))',
  },
}));

const GuidelinesIconButtonPosition = styled('div')(({ theme }) => ({
  left: 16,
  position: 'absolute',
  top: 16,
  zIndex: 22,

  [theme.breakpoints.down('sm')]: {
    left: 12,
    top: 12,
  },
}));

const GuidelinesIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(238, 238, 238, 0.94)',
  borderRadius: '50%',
  color: theme.palette.text.primary,
  height: 44,
  width: 44,

  '&:hover': {
    backgroundColor: 'rgba(238, 238, 238, 1)',
  },

  '& .MuiSvgIcon-root': {
    fontSize: 22,
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  height: 28,
  opacity: 0.68,
  width: 28,
  position: 'absolute',
  top: theme.spacing(0.5),
  right: theme.spacing(0.5),

  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    opacity: 1,
  },

  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
}));

const GuidelineRow = styled(Stack)({
  alignItems: 'flex-start',
});

interface PrintAreaSize {
  key: string
  label: string
  sizes: number[]
}

const formatScenePartSizeKey = (sceneSlug: string, partSlug: string) => {
  return sceneSlug + ':' + partSlug;
}

const buttonTransitionTimeout = {
  enter: 210,
  exit: 135,
};

const overlayTransitionTimeout = {
  enter: 270,
  exit: 180,
};

export default function DesignGuidelinesOverlay() {
  const [t] = useTranslation('design-lab');
  const theme = useTheme();
  const breakpointSmDown = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const [opened, setOpened] = useState(() => !breakpointSmDown);
  const activeScene = useAppSelector(state => state.get('UIData').get('designLab').get('activeScene'));
  const activeVariant = useAppSelector(state => state.get('UIData').get('designLab').get('activeVariant'));
  const labData = useLabData();

  const partNameBySlug = useMemo<Record<string, string>>(() => {
    return {
      default: t('Artwork'),
      back: t('Back'),
      front: t('Front'),
      left: t('Left'),
      right: t('Right'),
    }
  }, [t])

  const guidelines = useMemo(() => {
    if (!labData || activeScene === null) {
      return undefined;
    }

    const variantData = labData.variants[activeVariant] as LabVariantData | undefined;
    const sceneData = variantData?.scenes[activeScene];

    if (sceneData === undefined) {
      return undefined;
    }

    const partEntries = Object.entries(sceneData.parts);
    const printAreaSizes: PrintAreaSize[] = partEntries.map(([partSlug, partData]) => {
      const partName = partData.name ?? partSlug;
      const partLabel = partNameBySlug[partName] ?? partName;
      const label = partEntries.length === 1 && partSlug === 'default'
        ? t('Template size')
        : partLabel;

      return {
        key: formatScenePartSizeKey(activeScene, partSlug),
        label,
        sizes: [partData.render.width, partData.render.height],
      };
    });

    return {
      dpi: labData.dpi,
      printAreaSizes,
    };
  }, [activeScene, activeVariant, labData, partNameBySlug, t])

  if (guidelines === undefined || guidelines.printAreaSizes.length === 0) {
    return null;
  }

  return <>
    <Zoom
      in={!opened}
      style={{ transformOrigin: 'top left' }}
      timeout={buttonTransitionTimeout}
      unmountOnExit
    >
      <GuidelinesIconButtonPosition>
        <Tooltip title={t('Show design guidelines')} placement="top">
          <GuidelinesIconButton
            aria-label={t('Show design guidelines')}
            onClick={() => setOpened(true)}
            size="small"
          >
            <WallpaperRoundedIcon fontSize="small" />
          </GuidelinesIconButton>
        </Tooltip>
      </GuidelinesIconButtonPosition>
    </Zoom>
    <Zoom
      in={opened}
      style={{ transformOrigin: 'top left' }}
      timeout={overlayTransitionTimeout}
      unmountOnExit
    >
      <GuidelinesOverlay component="div">
        <CloseButton
          aria-label={t('Hide design guidelines')}
          onClick={() => setOpened(false)}
          size="small"
        >
          <CloseRoundedIcon />
        </CloseButton>
        <Stack spacing={1}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(0, 0, 0, 0.82)',
                fontWeight: 700,
                letterSpacing: 0.2,
                lineHeight: 1.2,
                textTransform: 'uppercase',
              }}
            >
              {t('Design guidelines')}
            </Typography>
            {guidelines.dpi > 0 ? <Typography
              variant="caption"
              sx={{
                color: 'rgba(0, 0, 0, 0.62)',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              {t('{{dpi}} DPI', { dpi: guidelines.dpi })}
            </Typography> : null}
          </Stack>
          <Stack spacing={0.5}>
            {guidelines.printAreaSizes.map(printAreaSize => {
              return <GuidelineRow
                direction="row"
                justifyContent="space-between"
                key={printAreaSize.key}
                spacing={2}
              >
                <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.72)', lineHeight: 1.3 }}>
                  {printAreaSize.label}
                </Typography>
                <Stack sx={{ alignItems: 'flex-end', lineHeight: 1.25, textAlign: 'right' }}>
                  {guidelines.dpi > 0 ? <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.88)', fontWeight: 700, lineHeight: 1.25 }}>
                    <SizeDisplay
                      sizes={printAreaSize.sizes}
                      sourceUnit="px"
                      dpi={guidelines.dpi}
                      displayType="physical"
                    />
                  </Typography> : null}
                  <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.56)', lineHeight: 1.25 }}>
                    <SizeDisplay
                      sizes={printAreaSize.sizes}
                      sourceUnit="px"
                      displayType="digital"
                    />
                  </Typography>
                </Stack>
              </GuidelineRow>
            })}
          </Stack>
        </Stack>
      </GuidelinesOverlay>
    </Zoom>
  </>
}
